import React, { useContext, useEffect, useRef, useState } from 'react';
import {
  ComponentRendering,
  Image,
  ImageField,
  Field,
  SitecoreContext,
  RichText,
  WithSitecoreContextProps,
} from '@sitecore-jss/sitecore-jss-react';
import { Button, Grid } from '@mui/material';
// eslint-disable-next-line import/no-extraneous-dependencies
import ReactToPrint from 'react-to-print';
import { addEllipsis, getCurrencyInUSD, getDateTimePartsFromIso } from '../../utils/helper';
import { getAPICall } from '../../dataFetcher/axiosMethodCalls';
import LoadingSection from '../LoadingSection/LoadingSection';
import nextId from 'react-id-generator';
import DOMPurify from 'dompurify';
import { GState, jsPDF } from 'jspdf';
import LoadingIcon from '../LoadingIcon/LoadingIcon';
import { APIWebstoreContext } from '../../context/globalContext';
import { PAYMENT_TYPES, PURCHASE_ORDER, TRAINING_IDENTIFIER } from '../../utils/constants';
import { MODALITY_TITLES } from '../../utils/ProductTypes';
import './index.scss';
import { IPaymentReceiptDetails, IReceiptItems } from './IPaymentReceipt';

export type PaymentReceiptProps = WithSitecoreContextProps & {
  fields: {
    PrintButtonText: Field<string>;
    DownloadButtonText: Field<string>;
    ContactSupport: Field<string>;
    RefundAdditionalText: Field<string>;
    MailingAddressText: Field<string>;
    WireTransferInstruction: Field<string>;
    BillingAddressText: Field<String>;
    LOBSpecificConfiguration: Array<LobConfigurationProps>;
  };
  sitecoreContext: SitecoreContext & { pageEditing: boolean };
  rendering: ComponentRendering;
};

export type LobConfigurationProps = {
  displayName: string;
  fields: {
    LOBIdentifier: Field<string>;
    CorrespondenceToAddress: Field<string>;
    InvoiceNotice: Field<string>;
    Logo: ImageField;
    Stamp: ImageField;
    CheckMailToAddress: Field<string>;
    WireTransferMailToAddress: Field<string>;
    FederalTaxInstructions: Field<string>;
  };
};

const PaymentReceipt = ({ fields, rendering }: PaymentReceiptProps): React.JSX.Element => {
  const printRef = useRef<HTMLDivElement>(null);
  const [paymentReceiptDetails, setPaymentReceiptDetails] = useState<IPaymentReceiptDetails>();
  const [isLoading, setIsLoading] = useState(true);
  const [isWorking, setIsWorking] = useState(false);
  const [downloadWidth, setDownloadWidth] = useState('1em');
  const [isRefund, setIsRefund] = useState(false);
  const { state } = useContext(APIWebstoreContext);

  const getLobConfig = (lob: string): LobConfigurationProps | undefined => {
    return fields?.LOBSpecificConfiguration?.find(
      (config) => config.fields?.LOBIdentifier?.value === lob
    );
  };

  function replaceFieldText(field: Field<string> | undefined): Field<string> | undefined {
    if (!field?.value) return undefined;

    return {
      ...field,
      value: field.value.replace('##INVOICE_NUMBER##', paymentReceiptDetails?.orderId || ''),
    };
  }

  function replaceText(): Field<string> {
    let refundAdditionalText = fields?.RefundAdditionalText?.value;
    if (refundAdditionalText !== undefined) {
      refundAdditionalText = refundAdditionalText.replace(
        '##CREDIT_CARD_NUMBER##',
        paymentReceiptDetails?.payment?.last4DigitsCCNumber || ''
      );
      refundAdditionalText = refundAdditionalText.replace(
        '##AMOUNT##',
        getCurrencyInUSD(paymentReceiptDetails?.total) || ''
      );
      fields.RefundAdditionalText.value = refundAdditionalText;
    }
    return fields?.RefundAdditionalText;
  }

  function convertDate(date: string) {
    return new Date(date).toLocaleDateString('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric',
    });
  }

  function getPaymentReceiptDetails() {
    let orderId;
    let search = window.location.search;
    let params = new URLSearchParams(search);
    if (params.get('orderid')) {
      orderId = params.get('orderid');
    }

    if (orderId === undefined || orderId === '') {
      setIsLoading(false);
      return;
    }

    if (params.get('refund')) {
      let refund = params.get('refund');
      if (refund !== undefined && refund !== null && refund) {
        setIsRefund(true);
      } else {
        setIsRefund(false);
      }
    }

    getAPICall(`${process.env.REACT_APP_CART_URL}/cartOrder/${orderId}/invoice`)
      .then(function (response: any) {
        if (response && response.status === 200) {
          setPaymentReceiptDetails(response.data);
        }
      })
      .finally(() => {
        setIsLoading(false);
      });
  }

  useEffect(() => {
    getPaymentReceiptDetails();
  }, []);

  function getInvoiceAddressSection(
    paymentType: string,
    paymentProcessed: boolean,
    isVerifoneOrder: boolean
  ) {
    if (paymentType === PURCHASE_ORDER && paymentProcessed === false && !isVerifoneOrder) {
      return (
        <div className="invoiceAddressSection">
          {paymentReceiptDetails &&
            (getLobConfig(paymentReceiptDetails.lob)?.fields?.CheckMailToAddress ||
              fields.ContactSupport.value) && (
              <div className="flex-start-justifybetween bottomSpacing invoiceAddressWidth">
                <Grid container className="mailAddressesArea">
                  <Grid item>
                    <RichText
                      field={replaceFieldText(
                        getLobConfig(paymentReceiptDetails.lob)?.fields?.CheckMailToAddress
                      )}
                    />
                  </Grid>
                  <Grid item>
                    <RichText field={fields?.ContactSupport} />
                  </Grid>
                </Grid>
              </div>
            )}
          {paymentReceiptDetails &&
            getLobConfig(paymentReceiptDetails.lob)?.fields?.WireTransferMailToAddress && (
              <div className="invoiceAddressWidth wireTransferWidth">
                <Grid container>
                  <Grid item>
                    <RichText
                      field={replaceFieldText(
                        getLobConfig(paymentReceiptDetails.lob)?.fields?.WireTransferMailToAddress
                      )}
                    />
                  </Grid>
                </Grid>
              </div>
            )}
          {paymentReceiptDetails &&
            getLobConfig(paymentReceiptDetails.lob)?.fields?.CorrespondenceToAddress && (
              <div className="invoiceAddressWidth wireTransferWidth">
                <Grid container>
                  <Grid item>
                    <RichText
                      field={replaceFieldText(
                        getLobConfig(paymentReceiptDetails.lob)?.fields?.CorrespondenceToAddress
                      )}
                    />
                  </Grid>
                </Grid>
              </div>
            )}
        </div>
      );
    } else {
      return <></>;
    }
  }

  function getFederalTaxInfo(federalTaxInstructions?: Field<string>) {
    const rawHtml = federalTaxInstructions?.value ?? '';
    const sanitizedHtml = DOMPurify.sanitize(rawHtml);

    return <div dangerouslySetInnerHTML={{ __html: sanitizedHtml }} />;
  }

  function getSealSection(
    paymentType?: string,
    isVerifoneOrder?: boolean,
    stampField?: ImageField
  ) {
    const renderStampImage = (stampField: ImageField) => {
      if (stampField?.value) {
        return <Image className="stampImage" field={stampField.value} />;
      }
      return <></>;
    };

    if (isVerifoneOrder) {
      return (
        <div>
          <div className="paid-info-section">
            {paymentType === PURCHASE_ORDER &&
              (fields?.WireTransferInstruction?.value || fields?.ContactSupport?.value) && (
                <div className="left-section">
                  <RichText className="wire-transfer-ins" field={fields?.WireTransferInstruction} />
                  <Grid className="wire-transfer-ins" item>
                    <RichText field={fields?.ContactSupport} />
                  </Grid>
                </div>
              )}
            {stampField && renderStampImage(stampField)}
          </div>
        </div>
      );
    } else {
      return <div className="paid-info">{stampField && renderStampImage(stampField)}</div>;
    }
  }

  const handleDownload = () => {
    if (typeof window !== 'undefined') {
      const html2pdf = require('html2pdf.js');
      setIsWorking(true);

      const content = printRef.current;

      if (content) {
        const options = {
          margin: 0,
          html2canvas: {
            useCORS: true,
            scale: 4,
            letterRendering: true,
          },
          jsPDF: {
            unit: 'pt',
            format: [content.offsetWidth / 1.3, content.offsetHeight / 1.2],
            orientation: 'portrait',
          },
        };

        html2pdf()
          .set(options)
          .from(content)
          .save(`Receipt-${paymentReceiptDetails?.orderId}.pdf`)
          .then(() => setIsWorking(false));
      }
    }
  };

  const InvoicePrinting = (): React.JSX.Element => {
    let paymentType = paymentReceiptDetails?.payment.paymentType;

    const creditCardDesc = `${paymentReceiptDetails?.payment?.cardType?.toUpperCase()} ending ${
      paymentReceiptDetails?.payment?.last4DigitsCCNumber
    }`;

    const verifonePaymentType =
      state?.sitecoreDictionaryData &&
      paymentReceiptDetails &&
      state?.sitecoreDictionaryData[paymentReceiptDetails?.payment?.verifonePaymentType];

    const paymentDescription =
      verifonePaymentType && verifonePaymentType.length > 0
        ? `${verifonePaymentType} ${
            paymentReceiptDetails?.payment?.last4DigitsCCNumber && creditCardDesc
          }`
        : `Credit Card: ${creditCardDesc}`;
    let paymentProcessed = paymentReceiptDetails?.payment.paymentProcessed;

    let refundReceipt = isRefund ? paymentReceiptDetails?.isOrderReturned : false;

    const shipToAddress = paymentReceiptDetails?.receiptItems?.[0].shipToAddress;

    function displayEmailAddresses(emailAddresses: Array<string>) {
      let emailAddressList: Array<string> = [];
      var count: any = {};

      emailAddresses.forEach(function (i) {
        count[i.toLowerCase()] = (count[i.toLowerCase()] || 0) + 1;
      });

      for (let [key, value] of Object.entries(count)) {
        emailAddressList.push(`${key} (${value})`);
      }

      return (
        <div key={nextId()}>
          <span key={nextId()}>{emailAddressList.join(', ')}</span>
        </div>
      );
    }

    const billingFirstName = paymentReceiptDetails?.billingAddress?.firstName;
    const billingLastName = paymentReceiptDetails?.billingAddress?.lastName;
    const hasBillingName = billingFirstName || billingLastName;
    const displayFirstName = hasBillingName ? billingFirstName : paymentReceiptDetails?.firstName;
    const displayLastName = hasBillingName ? billingLastName : paymentReceiptDetails?.lastname;
    return (
      <>
        <div className="invoice-content">
          <div className={`header ${refundReceipt ? 'red-content' : ''}`}>
            {refundReceipt
              ? 'Refund Receipt #'
              : paymentType === PURCHASE_ORDER && paymentProcessed === false
              ? 'Pro Forma Invoice #'
              : 'Receipt #'}
            {paymentReceiptDetails?.orderId}
            {paymentReceiptDetails?.orderId !== paymentReceiptDetails?.unifiedReferenceNumber && (
              <div className="invoice-ref-header">
                {'Reference #'}
                {paymentReceiptDetails?.unifiedReferenceNumber}
              </div>
            )}
          </div>

          {paymentReceiptDetails && getLobConfig(paymentReceiptDetails?.lob)?.fields?.Logo && (
            <Image
              className="api-logo"
              field={getLobConfig(paymentReceiptDetails.lob)?.fields?.Logo.value}
            />
          )}
        </div>
        <br />
        {paymentType === PURCHASE_ORDER && paymentProcessed === false && (
          <div className="content-100">
            {paymentReceiptDetails &&
              getLobConfig(paymentReceiptDetails?.lob)?.fields?.InvoiceNotice && (
                <RichText
                  field={replaceFieldText(
                    getLobConfig(paymentReceiptDetails.lob)?.fields?.InvoiceNotice
                  )}
                />
              )}
            <br />
          </div>
        )}

        <div className="flex-start-justifybetween">
          {shipToAddress && (
            <div>
              <div className="sub-header">{fields.MailingAddressText?.value}</div>
              <div className="address-content">
                <div>
                  {shipToAddress?.firstName} {shipToAddress?.lastName}
                </div>
                <div>{shipToAddress?.street1}</div>
                <div>{shipToAddress?.street2}</div>
                <div>
                  {shipToAddress?.city}, {shipToAddress?.state} {shipToAddress?.zip}
                </div>
                <div>{shipToAddress?.country}</div>
                <div>{shipToAddress?.phone}</div>
              </div>
            </div>
          )}
          {paymentReceiptDetails?.billingAddress && (
            <div>
              <div className="sub-header">{fields.BillingAddressText?.value}</div>
              <div className="address-content">
                {displayFirstName && displayLastName && (
                  <div>{`${displayFirstName} ${displayLastName}`}</div>
                )}
                {paymentReceiptDetails.companyName && (
                  <div>{paymentReceiptDetails.companyName}</div>
                )}
                <div>{paymentReceiptDetails?.billingAddress?.street1}</div>
                <div>{paymentReceiptDetails?.billingAddress?.street2}</div>
                <div>
                  {paymentReceiptDetails?.billingAddress?.city},{' '}
                  {paymentReceiptDetails?.billingAddress?.state}{' '}
                  {paymentReceiptDetails?.billingAddress?.zip}
                </div>
                <div>{paymentReceiptDetails?.billingAddress?.country?.toUpperCase()}</div>
                <div>{paymentReceiptDetails?.billingAddress?.phone}</div>
              </div>
            </div>
          )}
        </div>
        <br />
        <div className="border-100">
          <Grid container spacing={3}>
            {!refundReceipt ? (
              <Grid item>
                <div className="column-header">Date Ordered</div>
                {paymentReceiptDetails && convertDate(paymentReceiptDetails.dateOrdered)}
              </Grid>
            ) : (
              <Grid item>
                <div className="column-header">Date Refunded</div>
                {paymentReceiptDetails && convertDate(paymentReceiptDetails.dateRefunded)}
              </Grid>
            )}
            <Grid item>
              <div className="column-header">Invoice Number</div>
              {paymentReceiptDetails?.orderId}
            </Grid>
            {paymentReceiptDetails?.receiptItems &&
              paymentReceiptDetails.receiptItems[0] &&
              paymentReceiptDetails.receiptItems[0].icpContactId && (
                <Grid item>
                  <div className="column-header">ICP ID</div>
                  {paymentReceiptDetails.receiptItems[0].icpContactId}
                </Grid>
              )}
            <Grid item>
              <div className="column-header">Order Total</div>
              {getCurrencyInUSD(paymentReceiptDetails?.total)}
            </Grid>
            <Grid item>
              <div className="column-header">Order Status</div>
              {!refundReceipt
                ? state?.sitecoreDictionaryData &&
                  paymentReceiptDetails &&
                  state?.sitecoreDictionaryData[paymentReceiptDetails?.status]
                : (state?.sitecoreDictionaryData && state?.sitecoreDictionaryData?.Refunded) ||
                  'Refund'}
            </Grid>
            {!paymentReceiptDetails?.isNoCostOrder && (
              <Grid item>
                <div className="column-header">Payment Type</div>
                {paymentType === PURCHASE_ORDER
                  ? PAYMENT_TYPES[paymentReceiptDetails?.payment?.paymentMethod ?? '']
                  : paymentDescription}
              </Grid>
            )}
          </Grid>
        </div>
        {refundReceipt && <div className="container-refund-background">FULL REFUND</div>}
        <br />
        {paymentReceiptDetails &&
          paymentReceiptDetails?.receiptItems.map((item: IReceiptItems, id: number) => {
            return (
              <div key={nextId()}>
                <div className="flex-start-justifybetween title-section">
                  <div key={nextId()}>
                    <div className="sub-header" key={nextId()}>
                      {item?.productTitle}
                      {addEllipsis(item?.productTitle)}
                    </div>
                    {paymentReceiptDetails.lob !== TRAINING_IDENTIFIER && item?.productCode && (
                      <div key={nextId()}>Product Code: {item?.productCode}</div>
                    )}
                  </div>
                  <div className="item-total">
                    <div className="sub-header" key={nextId()}>
                      {getCurrencyInUSD(Number(item?.quantity) * Number(item?.unitPrice))}
                    </div>
                    {item?.surchargeType && (
                      <div className="sub-header" key={nextId()}>
                        <span>{item?.surchargeType}: </span>
                        {getCurrencyInUSD(Number(item?.surchargeCost))}
                      </div>
                    )}
                  </div>
                </div>

                <Grid container spacing={3} key={nextId()} className="detail-section">
                  {item?.format && (
                    <Grid item key={nextId()} style={{ minWidth: '25%' }}>
                      <div className="column-header" key={nextId()}>
                        Format
                      </div>
                      {item?.format}
                    </Grid>
                  )}
                  <Grid item key={nextId()} style={{ minWidth: '15%' }}>
                    <div className="column-header" key={nextId()}>
                      Quantity
                    </div>
                    {item?.quantity}
                  </Grid>

                  {item?.language && (
                    <Grid item key={nextId()} style={{ minWidth: '15%' }}>
                      <div className="column-header" key={nextId()}>
                        Language
                      </div>
                      {item.language}
                    </Grid>
                  )}

                  {item.modality && (
                    <Grid item style={{ minWidth: '15%' }}>
                      <div className="column-header">Modality</div>
                      {MODALITY_TITLES[item.modality]}
                    </Grid>
                  )}

                  {item?.examWindow && (
                    <Grid item key={nextId()} style={{ minWidth: '15%' }}>
                      <div className="column-header" key={nextId()}>
                        Exam Window
                      </div>
                      {item.examWindow}
                    </Grid>
                  )}

                  {item &&
                    item?.isDigitalItem === true &&
                    item?.digitalCopiesSendAddresses &&
                    item?.digitalCopiesSendAddresses.length > 0 && (
                      <Grid item key={nextId()}>
                        <div className="column-header" key={nextId()}>
                          {item.digitalCopySent ? 'Send to' : 'Recipient Email(s)'}
                        </div>
                        {displayEmailAddresses(item?.digitalCopiesSendAddresses)}
                      </Grid>
                    )}
                  {item && item.isShipmentItem && (
                    <Grid item key={nextId()}>
                      <div className="column-header" key={nextId()}>
                        Shipped to
                      </div>
                      {item?.shipToAddress && (
                        <div key={nextId()}>
                          <span key={nextId()}>
                            {item?.shipToAddress.street1 && <>{item?.shipToAddress.street1}, </>}
                            {item?.shipToAddress.street2 && <>{item?.shipToAddress.street2}, </>}
                            {item?.shipToAddress.city && <>{item?.shipToAddress.city}, </>}
                            {item?.shipToAddress.state && <>{item?.shipToAddress.state}, </>}
                            {item?.shipToAddress.country && <>{item?.shipToAddress.country}, </>}
                            {item?.shipToAddress.zip !== null && <>{item?.shipToAddress.zip}</>}
                          </span>
                        </div>
                      )}
                    </Grid>
                  )}
                </Grid>
                <Grid container spacing={3} className="detail-section detail-date-section">
                  {item.sessionStartDate && (
                    <Grid item className="session-details">
                      <div className="column-header session-dates">Starts: </div>
                      {getDateTimePartsFromIso(item?.sessionStartDate)?.date}
                    </Grid>
                  )}
                  {item.sessionEndDate && (
                    <Grid item className="session-details">
                      <div className="column-header session-dates">Ends: </div>
                      {getDateTimePartsFromIso(item?.sessionEndDate)?.date}
                    </Grid>
                  )}
                </Grid>
                {item.sessionDescription && (
                  <Grid
                    container
                    spacing={3}
                    key={nextId()}
                    className="detail-section description-section"
                  >
                    <Grid item>
                      <div className="column-header">Description: </div>
                      <span
                        className="product-value session-desc"
                        dangerouslySetInnerHTML={{
                          __html: DOMPurify.sanitize(item.sessionDescription || ''),
                        }}
                      />
                    </Grid>
                  </Grid>
                )}
                <br />
                <hr key={nextId()} />
              </div>
            );
          })}
        <br />
        <div className="flex-column-100">
          <div className="flex-start-justifybetween sub-header">
            <div>Subtotal</div>
            <div>{getCurrencyInUSD(paymentReceiptDetails?.subTotal)}</div>
          </div>
          {paymentReceiptDetails?.memberDiscount !== undefined &&
            paymentReceiptDetails?.memberDiscount > 0 && (
              <div className="flex-start-justifybetween sub-header">
                <div>Member Discount (30%)</div>
                <div>{getCurrencyInUSD(paymentReceiptDetails?.memberDiscount)}</div>
              </div>
            )}
          <div className="flex-start-justifybetween sub-header">
            <div>Tax</div>
            <div>{getCurrencyInUSD(paymentReceiptDetails?.taxTotal)}</div>
          </div>
          <div className="flex-start-justifybetween sub-header">
            <div>Shipping and Handling</div>
            <div>{getCurrencyInUSD(paymentReceiptDetails?.shippingTotal)}</div>
          </div>
        </div>
        <br />
        <hr />
        <div className="flex-start-justifybetween sub-header">
          <div>TOTAL</div>
          <div>{getCurrencyInUSD(paymentReceiptDetails?.total)}</div>
        </div>

        {paymentReceiptDetails &&
          getLobConfig(paymentReceiptDetails?.lob)?.fields?.Stamp &&
          getSealSection(
            paymentType,
            paymentReceiptDetails?.isVerifoneOrder,
            getLobConfig(paymentReceiptDetails?.lob)?.fields?.Stamp
          )}

        <div className="paid-info">
          {isRefund && paymentReceiptDetails?.isOrderReturned
            ? 'FULL REFUND'
            : paymentProcessed
            ? 'PAID IN FULL'
            : ''}
        </div>
        <div className="refundAdditionalText">
          {refundReceipt && <RichText field={replaceText()} />}
        </div>
        {paymentReceiptDetails && paymentReceiptDetails.isVerifoneOrder && (
          <div className="markupNote">
            <span
              dangerouslySetInnerHTML={{
                __html: state?.sitecoreDictionaryData?.InvoiceMarkupNote || '',
              }}
            ></span>
          </div>
        )}
        <div key={nextId()}>
          {paymentReceiptDetails &&
            getInvoiceAddressSection(
              paymentReceiptDetails.payment.paymentType,
              paymentReceiptDetails.payment.paymentProcessed,
              paymentReceiptDetails.isVerifoneOrder
            )}
        </div>
        <div key={nextId()} className="flex-start-justifybetween sub-header sub-header-no-bold">
          {paymentReceiptDetails &&
            getFederalTaxInfo(
              getLobConfig(paymentReceiptDetails.lob)?.fields?.FederalTaxInstructions
            )}
        </div>
      </>
    );
  };

  let refundReceipt = isRefund ? paymentReceiptDetails?.isOrderReturned : false;
  return (
    <div>
      {isLoading ? (
        <div className="invoiceLoadingSection">
          <LoadingSection open={isLoading} size="fullSize" message="Please wait..." />
        </div>
      ) : paymentReceiptDetails?.orderId === undefined || paymentReceiptDetails?.orderId === '' ? (
        <>
          <div className="invoice-container">
            <div className="base-print-size">
              <div className="invoice-receipt" style={{ fontSize: downloadWidth }}>
                <div className="invoice-content">
                  <div className="header red-content">Order Number Not Found</div>
                  {paymentReceiptDetails &&
                    getLobConfig(paymentReceiptDetails?.lob)?.fields?.Logo && (
                      <Image
                        className="api-logo"
                        field={getLobConfig(paymentReceiptDetails?.lob)?.fields?.Logo.value}
                      />
                    )}
                </div>
              </div>
            </div>
          </div>
        </>
      ) : (
        <>
          <div className="invoice-container" ref={printRef}>
            <div className="base-print-size">
              <div className="invoice-receipt" style={{ fontSize: downloadWidth }}>
                <InvoicePrinting />
              </div>
            </div>
          </div>
          <div className="printAndDownload">
            <ReactToPrint
              trigger={() => (
                <Button variant="contained" name="print" className="ws-primary-button-no-border">
                  {fields.PrintButtonText.value}
                </Button>
              )}
              content={() => printRef.current}
            />

            <Button
              variant="contained"
              name="download"
              className="ws-white-button"
              {...(!isWorking ? { onClick: handleDownload } : {})}
            >
              {isWorking ? <LoadingIcon /> : fields.DownloadButtonText.value}
            </Button>
          </div>
        </>
      )}
    </div>
  );
};

export default PaymentReceipt;
